import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useWhereAmIStore } from "@/stores/useWhereAmIStore";
import { useGuidesStore } from "@/stores/useGuidesStore";
import { computeYouAreHere } from "@/hooks/useStationActivity";
import { usePapersCount } from "@/hooks/usePapersCount";
import { useCycleSignals } from "@/hooks/useCycleSignals";
import { useConceptSnapshot } from "@/hooks/useConceptSnapshot";
import { computeRecommendations, type WorkflowRecommendation } from "@/lib/workflowAdvisor";
import { SubwayMap } from "./SubwayMap";
import { WorkflowPanel } from "./WorkflowPanel";
import { ENACTED, type StationId, type TabId, type RouteId } from "./registry";

interface Props {
  currentTab: TabId;
  conceptId: string | null;
}

export function WhereAmIOverlay({ currentTab, conceptId }: Props) {
  const isOpen = useWhereAmIStore((s) => s.isOpen);
  const close = useWhereAmIStore((s) => s.close);
  const { data: counts } = usePapersCount(conceptId ?? undefined);
  const { data: cycleSignals } = useCycleSignals(conceptId ?? undefined);
  const navigate = useNavigate();
  const [hover, setHover] = useState<StationId | null>(null);

  const queryClient = useQueryClient();
  // The map is pull-based; the papers-count query isn't otherwise refreshed by
  // papers mutations (it's keyed outside the ["papers", id] family on purpose),
  // so refetch it each time the overlay opens to avoid a stale snapshot.
  useEffect(() => {
    if (isOpen && conceptId) {
      queryClient.invalidateQueries({ queryKey: ["papers-count", conceptId] });
    }
  }, [isOpen, conceptId, queryClient]);

  const snapshot = useConceptSnapshot(conceptId ?? undefined);
  const recs = snapshot ? computeRecommendations(snapshot) : [];

  const actOnRec = (rec: WorkflowRecommendation) => {
    if (!conceptId || rec.status === "blocked") return;
    close();
    navigate({ to: rec.action.to, params: { conceptId }, search: rec.action.search ?? {} } as never);
  };

  // Deep-link a recommendation's guide: close the map, open the walkthrough,
  // scrolling to the rec's section when one is provided.
  const openGuide = (walkthroughId: string, section?: string) => {
    close();
    useGuidesStore.getState().openWalkthrough(walkthroughId, section);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        close();
        return;
      }
      if (e.key === "?" && !isOpen) {
        const el = document.activeElement;
        if (
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          (el instanceof HTMLElement &&
            (el.isContentEditable || el.getAttribute("contenteditable") != null))
        ) {
          return;
        }
        useWhereAmIStore.getState().open();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const signals = counts
    ? { foundational: counts.foundational, trawled: counts.trawled, scored: counts.scored }
    : null;
  const youAreHere = computeYouAreHere(currentTab, signals);
  const enactedRouteIds: ReadonlySet<RouteId> = new Set(
    cycleSignals
      ? (Object.keys(ENACTED) as RouteId[]).filter((id) => ENACTED[id]!(cycleSignals))
      : [],
  );

  return (
    <>
      <div
        data-testid="where-am-i-backdrop"
        className="fixed inset-0 z-40"
        style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)" }}
        onClick={close}
      />
      <div
        role="dialog"
        aria-label="Where am I?"
        className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-card"
        style={{
          width: "min(1120px, calc(100% - 32px))",
          boxShadow: "0 40px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(180,190,254,0.05)",
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border px-5 pb-3 pt-4">
          <div>
            <div className="text-[18px] font-semibold text-foreground" style={{ letterSpacing: "-0.005em" }}>
              The map
            </div>
            <div className="mt-0.5 text-[11.5px] text-muted-foreground">6 stations · 6 routes</div>
          </div>
          <span className="flex-1" />
          <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
            Esc
          </span>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </div>

        {/* Body grid */}
        <div className="grid grid-cols-[1fr_380px]">
          <div
            className="px-5 pb-5 pt-4"
            style={{
              background: "radial-gradient(60% 60% at 40% 70%, color-mix(in srgb, var(--primary) 5%, transparent), transparent 70%)",
            }}
          >
            <SubwayMap
              youAreHereStationId={youAreHere}
              hoveredStationId={hover}
              onHoverStation={setHover}
              enactedRouteIds={enactedRouteIds}
            />
          </div>

          <div
            className="flex flex-col gap-3 border-l border-border p-4"
            style={{ background: "color-mix(in srgb, var(--surface-crust) 50%, transparent)" }}
          >
            {snapshot ? (
              <WorkflowPanel snapshot={snapshot} recs={recs} onAct={actOnRec} onOpenGuide={openGuide} />
            ) : (
              <div className="text-xs text-muted-foreground">Loading…</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
